import { useRef, useCallback } from 'react';

const MIN_SWIPE_X = 50;
const MAX_SWIPE_Y = 80; // ignore if vertical intent

interface SwipeHandlers {
  onTouchStart: (e: React.TouchEvent) => void;
  onTouchEnd: (e: React.TouchEvent) => void;
}

/**
 * Detects horizontal swipe gestures and calls onSwipeLeft / onSwipeRight.
 * Ignores swipes that are more vertical than horizontal (so scrolling still works).
 */
export function useSwipeNavigation(
  onSwipeLeft: () => void,
  onSwipeRight: () => void
): SwipeHandlers {
  const startRef = useRef<{ x: number; y: number } | null>(null);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    startRef.current = { x: touch.clientX, y: touch.clientY };
  }, []);

  const onTouchEnd = useCallback((e: React.TouchEvent) => {
    if (!startRef.current) return;
    const touch = e.changedTouches[0];
    const dx = touch.clientX - startRef.current.x;
    const dy = Math.abs(touch.clientY - startRef.current.y);
    startRef.current = null;

    // Ignore if vertical movement is larger (user was scrolling)
    if (dy > MAX_SWIPE_Y) return;
    // Ignore if horizontal movement too small
    if (Math.abs(dx) < MIN_SWIPE_X) return;

    if (dx < 0) {
      onSwipeLeft(); // swiped left → next
    } else {
      onSwipeRight(); // swiped right → prev
    }
  }, [onSwipeLeft, onSwipeRight]);

  return { onTouchStart, onTouchEnd };
}
